import React, { useState, useEffect } from 'react';
import {
  Search, Truck, Handshake, Phone, Mail, Trash2,
  ChevronDown, ChevronUp, Loader2, MessageSquare
} from 'lucide-react';
import { useAdmin } from './AdminContext';

export default function SubmissionsPage() {
  const { token, apiUrl, logout, authHeaders } = useAdmin();
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [expandedId, setExpandedId] = useState(null);

  const fetchSubmissions = async () => {
    try {
      const res = await fetch(`${apiUrl}/api/contact/submissions`);
      setSubmissions(await res.json());
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchSubmissions(); }, []);

  const updateStatus = async (id, status) => {
    await fetch(`${apiUrl}/api/contact/submissions/${id}/status?status=${status}`, {
      method: 'PATCH',
      headers: authHeaders(),
    });
    fetchSubmissions();
  };

  const deleteSub = async (id) => {
    if (!window.confirm('Delete this submission?')) return;
    await fetch(`${apiUrl}/api/contact/submissions/${id}`, {
      method: 'DELETE',
      headers: authHeaders(),
    });
    fetchSubmissions();
  };

  const filtered = submissions.filter(s => {
    if (typeFilter !== 'all' && s.type !== typeFilter) return false;
    if (statusFilter !== 'all' && s.status !== statusFilter) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      return (
        s.name?.toLowerCase().includes(q) ||
        s.company?.toLowerCase().includes(q) ||
        s.email?.toLowerCase().includes(q)
      );
    }
    return true;
  });

  const formatDate = (d) => new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 text-[#4fd1c5] animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-5 max-w-5xl" data-testid="admin-submissions-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-white">Submissions</h2>
          <p className="text-gray-500 text-sm mt-0.5">{submissions.length} total leads</p>
        </div>
        <div className="relative">
          <Search className="w-4 h-4 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            placeholder="Search name, company, email..."
            data-testid="submissions-search"
            className="pl-9 pr-4 py-2 bg-white/[0.04] border border-white/[0.08] rounded-lg text-white text-sm placeholder-gray-600 focus:outline-none focus:border-[#4fd1c5]/40 w-72 transition-colors"
          />
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2">
        <div className="flex bg-white/[0.04] rounded-lg p-0.5">
          {['all', 'pickup', 'partner'].map(f => (
            <button
              key={f}
              onClick={() => setTypeFilter(f)}
              data-testid={`filter-type-${f}`}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                typeFilter === f
                  ? 'bg-[#4fd1c5] text-[#0a1628] shadow-sm'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              {f === 'all' ? 'All Types' : f === 'pickup' ? 'Pickup' : 'Partner'}
            </button>
          ))}
        </div>
        <div className="flex bg-white/[0.04] rounded-lg p-0.5">
          {['all', 'new', 'in_progress', 'resolved'].map(s => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              data-testid={`filter-status-${s}`}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                statusFilter === s
                  ? 'bg-[#4fd1c5] text-[#0a1628] shadow-sm'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              {s === 'all' ? 'All Status' : s.replace('_', ' ')}
            </button>
          ))}
        </div>
      </div>

      {/* List */}
      <div className="space-y-2" data-testid="admin-submissions-list">
        {filtered.length === 0 ? (
          <div className="bg-white/[0.03] border border-white/[0.06] rounded-xl p-12 text-center">
            <MessageSquare className="w-8 h-8 text-gray-700 mx-auto mb-2" />
            <p className="text-gray-500 text-sm">No submissions match your filters.</p>
          </div>
        ) : (
          filtered.map(sub => (
            <div
              key={sub.id}
              className={`bg-white/[0.03] border rounded-xl overflow-hidden transition-all ${
                expandedId === sub.id ? 'border-[#4fd1c5]/20' : 'border-white/[0.06] hover:border-white/[0.1]'
              }`}
              data-testid={`submission-${sub.id}`}
            >
              {/* Row */}
              <div
                className="flex items-center gap-3 p-4 cursor-pointer"
                onClick={() => setExpandedId(expandedId === sub.id ? null : sub.id)}
              >
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                  sub.type === 'pickup' ? 'bg-blue-500/15' : 'bg-violet-500/15'
                }`}>
                  {sub.type === 'pickup'
                    ? <Truck className="w-5 h-5 text-blue-400" />
                    : <Handshake className="w-5 h-5 text-violet-400" />
                  }
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h4 className="text-white text-sm font-semibold truncate">{sub.name}</h4>
                    <span className="text-gray-600 text-xs hidden sm:inline">-</span>
                    <span className="text-gray-400 text-xs truncate hidden sm:inline">{sub.company}</span>
                  </div>
                  <p className="text-gray-500 text-xs mt-0.5 truncate">{sub.email}</p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className={`px-2 py-0.5 text-[9px] font-bold rounded-full uppercase ${
                    sub.status === 'new' ? 'bg-amber-500/15 text-amber-400' :
                    sub.status === 'in_progress' ? 'bg-blue-500/15 text-blue-400' :
                    'bg-green-500/15 text-green-400'
                  }`}>
                    {sub.status?.replace('_', ' ')}
                  </span>
                  <span className="text-gray-600 text-xs hidden sm:block">{formatDate(sub.created_at)}</span>
                  {expandedId === sub.id
                    ? <ChevronUp className="w-4 h-4 text-gray-500" />
                    : <ChevronDown className="w-4 h-4 text-gray-500" />
                  }
                </div>
              </div>

              {/* Expanded */}
              {expandedId === sub.id && (
                <div className="border-t border-white/[0.04] bg-white/[0.01] p-4 space-y-4">
                  <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                    {sub.phone && (
                      <a href={`tel:${sub.phone}`} className="flex items-center gap-2 text-gray-400 hover:text-[#4fd1c5] text-xs transition-colors">
                        <Phone className="w-3.5 h-3.5" /> {sub.phone}
                      </a>
                    )}
                    <a href={`mailto:${sub.email}`} className="flex items-center gap-2 text-gray-400 hover:text-[#4fd1c5] text-xs transition-colors">
                      <Mail className="w-3.5 h-3.5" /> {sub.email}
                    </a>
                    {sub.asset_type && <p className="text-gray-400 text-xs"><span className="text-gray-600">Asset:</span> {sub.asset_type}</p>}
                    {sub.quantity && <p className="text-gray-400 text-xs"><span className="text-gray-600">Qty:</span> {sub.quantity}</p>}
                  </div>
                  {sub.message && (
                    <p className="text-gray-400 text-xs bg-white/[0.03] rounded-lg p-3">{sub.message}</p>
                  )}
                  <div className="flex items-center justify-between">
                    <div className="flex gap-1.5">
                      {['new', 'in_progress', 'resolved'].map(s => (
                        <button
                          key={s}
                          onClick={() => updateStatus(sub.id, s)}
                          data-testid={`status-btn-${s}`}
                          className={`px-3 py-1.5 rounded-lg text-[10px] font-semibold transition-all ${
                            sub.status === s
                              ? 'bg-[#4fd1c5] text-[#0a1628]'
                              : 'bg-white/[0.04] text-gray-400 hover:bg-white/[0.08] hover:text-white'
                          }`}
                        >
                          {s.replace('_', ' ')}
                        </button>
                      ))}
                    </div>
                    <button
                      onClick={() => deleteSub(sub.id)}
                      data-testid={`delete-submission-${sub.id}`}
                      className="p-2 text-gray-600 hover:text-red-400 hover:bg-red-500/10 transition-colors rounded-lg"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
